import React, { useState, useEffect, useMemo } from 'react'
import { useLocation } from 'react-router'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Modal,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import PrintRoundedIcon from '@mui/icons-material/PrintRounded'
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded'

import { digitalAppApi } from '../../../../store/services/digitalAppApi'
import IvaPriceSwitch from '../../../../components/IvaPriceSwitch/IvaPriceSwitch'
import AlbaranPdfPanel from '../../../../components/AlbaranPdfPanel/AlbaranPdfPanel'
import './AlbaranPage.scss'
import {
  formatTruncatedNumber,
  getLineTotal,
  truncateToDecimals,
} from '../../../../utils/orderLineCalculations'

const formatMoney = (n) => `${formatTruncatedNumber(n)} €`

function SummaryRow({ label, value, valueColor, emphasis, dense }) {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={{ xs: 0.25, sm: 1 }}
      sx={{
        py: dense ? 1 : 1.25,
        borderBottom: '1px solid var(--albaran-border, #e5e7eb)',
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      <Typography
        component="span"
        variant="caption"
        sx={{
          color: 'text.secondary',
          textTransform: 'uppercase',
          letterSpacing: '.06em',
          fontWeight: 600,
          flex: { sm: '0 0 160px' },
        }}
      >
        {label}
      </Typography>
      <Typography
        component="span"
        variant={emphasis ? 'body1' : 'body2'}
        sx={{
          fontWeight: emphasis ? 700 : 400,
          color: valueColor ?? 'text.primary',
          wordBreak: 'break-word',
        }}
      >
        {value}
      </Typography>
    </Stack>
  )
}

function PdfCtaSection({ onOpenPdf, fullWidthSticky, disableStartIcon }) {
  return (
    <Button
      variant="contained"
      size="large"
      fullWidth
      onClick={onOpenPdf}
      {...(disableStartIcon ? {} : { startIcon: <PrintRoundedIcon /> })}
      sx={{
        bgcolor: '#5FC56E',
        fontWeight: 700,
        borderRadius: 2,
        py: fullWidthSticky ? 1.75 : 1.5,
        textTransform: 'none',
        fontSize: fullWidthSticky ? '1rem' : undefined,
        boxShadow: fullWidthSticky ? '0 -2px 12px rgba(0,0,0,.06)' : undefined,
        '&:hover': { bgcolor: '#4eb35e' },
      }}
    >
      Ver / descargar PDF
    </Button>
  )
}

export const AlbaranPage = () => {
  const theme = useTheme()
  const isNarrowMobile = useMediaQuery('(max-width:768px)')
  const useStickyPdfBar = useMediaQuery(theme.breakpoints.down('md'))

  const location = useLocation()
  const [pdfModalOpen, setPdfModalOpen] = useState(false)

  const orderId = useMemo(
    () => location.pathname.split('/').filter(Boolean).pop() || '',
    [location.pathname],
  )

  const {
    data: order,
    isLoading,
    isError,
  } = digitalAppApi.useGetOrderByIdQuery(orderId, {
    pollingInterval: 50000,
    skip: !orderId,
  })

  const [deliveryDate, setDeliveryDate] = useState('')
  const [creationDate, setCreationDate] = useState('')
  const [creationTime, setCreationTime] = useState('')
  const [items, setItems] = useState([])

  useEffect(() => {
    if (!order) return
    document.title = `Albarán · ${order._id.slice(-6)} ${order?.tradeName || order.userAcc?.tradeName || ''}`
    setDeliveryDate(order.deliveryDate ? order.deliveryDate : '')
    setItems(order.items || [])
    const date = new Date(order.date)
    setCreationDate(date.toLocaleDateString())
    setCreationTime(date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }))
  }, [order])

  const IVA = useMemo(() => {
    if (!order?.items?.length) return 0
    return truncateToDecimals(
      order.items.reduce((acc, item) => {
        const lineTotal = getLineTotal(item)
        const lineIva = item.IVA ? truncateToDecimals((Number(item.IVA) / 100) * lineTotal, 2) : 0
        return acc + lineIva
      }, 0),
      2,
    )
  }, [order])

  const balance = useMemo(() => {
    if (!order?.items) return '0.00'
    const delivery = truncateToDecimals(order?.deliveryPrice || 0, 2)
    const itemsSum = truncateToDecimals(order.items.reduce((acc, row) => acc + getLineTotal(row), 0), 2)
    const paid = truncateToDecimals(order?.paidValue || 0, 2)
    if (paid) {
      return formatTruncatedNumber(delivery + itemsSum - paid)
    }
    return formatTruncatedNumber(delivery + itemsSum)
  }, [order])

  const albrandDownload = useMemo(
    () => new URLSearchParams(location.search).get('albranddownload'),
    [location.search],
  )

  useEffect(() => {
    if (albrandDownload && order) {
      setPdfModalOpen(true)
    }
  }, [albrandDownload, order])

  const balanceNum = parseFloat(balance) || 0
  const openPdfModal = () => setPdfModalOpen(true)

  if (!orderId) {
    return (
      <Box className="albaranPage">
        <Alert severity="warning" role="status">
          No se ha encontrado el identificador del pedido en la URL.
        </Alert>
      </Box>
    )
  }

  if (isLoading && !order) {
    return (
      <Box className="albaranPage">
        <Box className="albaranPage__loadingBox">
          <Stack alignItems="center" spacing={2}>
            <CircularProgress sx={{ color: '#5FC56E' }} aria-label="Cargando pedido" />
            <Typography variant="body2" color="text.secondary">
              Cargando datos del pedido…
            </Typography>
          </Stack>
        </Box>
      </Box>
    )
  }

  if (isError || !order) {
    return (
      <Box className="albaranPage">
        <Alert severity="error" role="alert">
          No se ha podido cargar el pedido. Compruebe el enlace o intente más tarde.
        </Alert>
      </Box>
    )
  }

  return (
    <Box
      className="albaranPage"
      component="main"
      sx={{
        pb: useStickyPdfBar
          ? {
              xs: 'calc(100px + env(safe-area-inset-bottom))',
              sm: 'calc(100px + env(safe-area-inset-bottom))',
              md: 0,
            }
          : 0,
      }}
    >
      <Stack spacing={isNarrowMobile ? 2 : 3}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: isNarrowMobile ? 0.5 : 1 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap' }}>
            <DescriptionRoundedIcon sx={{ color: '#176318', fontSize: isNarrowMobile ? 26 : 28 }} aria-hidden />
            <Typography
              variant={isNarrowMobile ? 'h6' : 'h5'}
              component="h1"
              sx={{ fontWeight: 700, color: '#176318', letterSpacing: '-0.02em' }}
            >
              Albarán
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
            Revise los datos del pedido y genere el PDF del albarán.
          </Typography>
          {isNarrowMobile && (
            <Typography variant="caption" color="text.secondary">
              Pulse el botón verde inferior para abrir el PDF.
            </Typography>
          )}
        </Box>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            border: '1px solid var(--albaran-border, #e5e7eb)',
            overflow: 'hidden',
          }}
        >
          <Stack
            direction="row"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
            spacing={2}
            sx={{
              bgcolor: '#e8f5e9',
              px: { xs: 1.5, sm: 2.5 },
              py: { xs: 1.5, sm: 2 },
              borderBottom: '1px solid var(--albaran-border)',
            }}
          >
            <div>
              <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
                Pedido
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {order.idTucomida || `#${order._id?.slice(-6)}`}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {order?.tradeName || order?.userAcc?.tradeName || '—'}
              </Typography>
            </div>
            <Chip
              label={(order.status?.name || 'En curso').toString()}
              sx={{
                textTransform: 'capitalize',
                fontWeight: 600,
                bgcolor: order.status?.color || '#fff',
                border: '1px solid',
                borderColor: order.status?.color ? `${order.status.color}88` : 'divider',
                alignSelf: { xs: 'stretch', md: 'center' },
              }}
              variant="filled"
            />
          </Stack>

          <Box sx={{ px: { xs: 1.5, sm: 2.5 }, py: isNarrowMobile ? 0.5 : 1 }}>
            {!isNarrowMobile && (
              <SummaryRow
                label="Forma de pago"
                value={
                  order.paymentType === '-'
                    ? 'Sin cambio'
                    : !Number.isNaN(parseFloat(order.paymentType)) && parseFloat(order.paymentType) > 0
                      ? `Cambio desde ${order.paymentType} €`
                      : order.paymentType
                }
              />
            )}
            <SummaryRow
              dense={isNarrowMobile}
              label="Pendiente / saldo"
              value={formatMoney(balanceNum)}
              valueColor={balanceNum <= 0 ? 'success.main' : 'error.main'}
              emphasis
            />
            {!isNarrowMobile && (
              <>
                <SummaryRow label="IVA (estimación)" value={formatMoney(IVA)} />
                <SummaryRow label="Entrega" value={formatMoney(order.deliveryPrice || 0)} />
                <SummaryRow label="Pagado" value={formatMoney(order.paidValue)} />
                <SummaryRow label="Creado" value={`${creationDate} · ${creationTime}`} />
                <SummaryRow label="Entrega prevista" value={deliveryDate || '—'} />
                <SummaryRow label="Ítems" value={String(items.length)} />
                <SummaryRow label="Comentario" value={order.description?.trim() || 'Sin comentario'} />
              </>
            )}
            {isNarrowMobile && (
              <>
                <SummaryRow
                  dense
                  label="Forma de pago"
                  value={
                    order.paymentType === '-'
                      ? 'Sin cambio'
                      : !Number.isNaN(parseFloat(order.paymentType)) && parseFloat(order.paymentType) > 0
                        ? `Cambio desde ${order.paymentType} €`
                        : order.paymentType
                  }
                />
                <SummaryRow dense label="IVA" value={formatMoney(IVA)} />
                <SummaryRow
                  dense
                  label="Entrega / pagado"
                  value={`${formatMoney(order.deliveryPrice || 0)} · ${formatMoney(order.paidValue)}`}
                />
                <SummaryRow dense label="Ítems" value={String(items.length)} />
                <SummaryRow
                  dense
                  label="Prevista / creado"
                  value={`${(deliveryDate || '—').toString().slice(0, 36)} · ${creationDate}`}
                />
              </>
            )}
          </Box>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            border: '1px solid var(--albaran-border)',
            px: { xs: 1.5, sm: 2.5 },
            py: { xs: 1.25, sm: 2 },
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, display: { xs: 'none', sm: 'block' } }}>
            IVA en el documento (servidor)
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' }, mb: 1.5 }}>
            Por defecto sin IVA. El PDF se regenera al cambiar el interruptor en la ventana de descarga.
          </Typography>
          <IvaPriceSwitch compact={isNarrowMobile} />
        </Paper>

        <Divider sx={{ display: useStickyPdfBar ? 'none' : 'block' }} />

        {!useStickyPdfBar && (
          <Stack spacing={2} className="albaranPage__ctaStack">
            <PdfCtaSection onOpenPdf={openPdfModal} />
          </Stack>
        )}
      </Stack>

      {useStickyPdfBar && (
        <Box
          component="aside"
          className="albaranPage__stickyPdfBar"
          sx={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: theme.zIndex.appBar + 2,
            px: 2,
            pt: 1,
            pb: 'max(14px, env(safe-area-inset-bottom))',
            bgcolor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 -8px 24px rgba(0,0,0,.08)',
          }}
        >
          <PdfCtaSection onOpenPdf={openPdfModal} disableStartIcon fullWidthSticky />
        </Box>
      )}

      <Modal open={pdfModalOpen} onClose={() => setPdfModalOpen(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95%', sm: 560, md: 720 },
            maxHeight: '92vh',
            overflowY: 'auto',
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
          }}
        >
          <AlbaranPdfPanel
            orderId={order._id}
            tradeName={order?.tradeName || order?.userAcc?.tradeName}
            order={order}
            title="Print Albaran"
            showClose
            onClose={() => setPdfModalOpen(false)}
          />
        </Box>
      </Modal>
    </Box>
  )
}
